/**
 * Strip HTML tags and decode common entities so announcement / notice text
 * is stored and shown as plain text (no executable markup).
 */
export function stripHtmlToPlainText(input: unknown): string {
  const raw = String(input ?? '');
  const stripped = stripHtmlTags(raw.trim());
  const decoded = decodeBasicHtmlEntities(stripped);
  // Second pass catches markup that only appeared after entity decoding.
  return stripHtmlTags(decoded).trim();
}

function stripHtmlTags(input: string): string {
  let out = '';
  let inTag = false;
  let tagBuf = '';

  for (const c of input) {
    if (!inTag) {
      if (c === '<') {
        inTag = true;
        tagBuf = '<';
      } else {
        out += c;
      }
      continue;
    }

    tagBuf += c;
    if (c === '>') {
      const second = tagBuf.charAt(1);
      const isTag =
        tagBuf.length >= 2 &&
        (/[A-Za-z]/.test(second) || second === '/' || second === '!' || second === '?');
      if (!isTag) out += tagBuf;
      inTag = false;
      tagBuf = '';
    } else if (tagBuf.length > 256) {
      out += tagBuf;
      inTag = false;
      tagBuf = '';
    }
  }

  if (inTag) out += tagBuf;
  return out;
}

function decodeBasicHtmlEntities(input: string): string {
  return input.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (entity, body: string) => {
    switch (entity) {
      case '&amp;':
        return '&';
      case '&lt;':
        return '<';
      case '&gt;':
        return '>';
      case '&quot;':
        return '"';
      case '&apos;':
      case '&#39;':
      case '&#x27;':
      case '&#X27;':
        return "'";
      case '&#34;':
      case '&#x22;':
      case '&#X22;':
        return '"';
      default:
        break;
    }
    if (body.charAt(0) === '#') {
      const numeric = body.slice(1);
      const code =
        numeric.charAt(0) === 'x' || numeric.charAt(0) === 'X'
          ? Number.parseInt(numeric.slice(1), 16)
          : Number.parseInt(numeric, 10);
      if (Number.isFinite(code)) {
        try {
          return String.fromCodePoint(code);
        } catch {
          return entity;
        }
      }
    }
    return entity;
  });
}
